import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const CARE_TYPES = ["Independent Living", "Assisted Living", "Memory Care", "Skilled Nursing", "Post-Surgery Rehab", "Hospice / Palliative", "Respite (Short-term)", "Not sure yet"];
const TIMELINES = ["Immediately (within days)", "1–2 weeks", "Within a month", "1–3 months", "Just researching"];
const PAYMENTS = ["Private pay / out of pocket", "Long-term care insurance", "Medicaid", "Medicare", "VA benefits", "Not sure yet"];
const RELATIONSHIPS = ["Son / Daughter", "Spouse / Partner", "Other family member", "Discharge planner / Social worker", "Self (the resident)", "Other"];

const REQUIRED = {
  name: "Please enter your name.", phone: "Please enter a phone number.", email: "Please enter your email.",
  relationship: "Please select your relationship.", resident_name: "Please enter the resident's name.",
  resident_age: "Please enter the resident's age.", care_type: "Please select a care type.",
  timeline: "Please select a timeline.", payment: "Please select a payment source.", message: "Please add some details.",
};

export function InquiryDialog({ facility, open, onOpenChange, user }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "",
    relationship: "", resident_name: "", resident_age: "",
    care_type: "", timeline: "", payment: "", message: "",
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const errAttrs = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `inquiry-${k}-err` : undefined });
  const ErrText = ({ k }) => errors[k] ? <p id={`inquiry-${k}-err`} className="text-xs text-[#C0392B]">{errors[k]}</p> : null;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    Object.keys(REQUIRED).forEach((k) => { if (!String(form[k] || "").trim()) errs[k] = REQUIRED[k]; });
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please complete all fields before sending."); return; }
    setLoading(true);
    try {
      await api.post(`/facilities/${facility.facility_id}/inquiry`, form);
      toast.success("Inquiry sent to " + facility.name);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Contact {facility?.name}</DialogTitle>
          <DialogDescription>Share the resident's care needs so the right team can follow up quickly.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#0077B6]">Your details</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="inquiry-name">Your name</Label>
                <Input id="inquiry-name" required data-testid="inquiry-name" {...errAttrs("name")} value={form.name} onChange={(e) => set("name")(e.target.value)} />
                <ErrText k="name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inquiry-phone">Phone</Label>
                <Input id="inquiry-phone" required data-testid="inquiry-phone" {...errAttrs("phone")} value={form.phone} onChange={(e) => set("phone")(e.target.value)} />
                <ErrText k="phone" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inquiry-email">Email</Label>
              <Input id="inquiry-email" required type="email" data-testid="inquiry-email" {...errAttrs("email")} value={form.email} onChange={(e) => set("email")(e.target.value)} />
              <ErrText k="email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inquiry-relationship">Your relationship to the resident</Label>
              <Select value={form.relationship} onValueChange={set("relationship")}>
                <SelectTrigger id="inquiry-relationship" data-testid="inquiry-relationship" {...errAttrs("relationship")}><SelectValue placeholder="Select one" /></SelectTrigger>
                <SelectContent>{RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <ErrText k="relationship" />
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#0077B6]">Resident details</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="inquiry-resident-name">Resident name</Label>
                <Input id="inquiry-resident-name" required data-testid="inquiry-resident-name" {...errAttrs("resident_name")} value={form.resident_name} onChange={(e) => set("resident_name")(e.target.value)} />
                <ErrText k="resident_name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inquiry-resident-age">Resident age</Label>
                <Input id="inquiry-resident-age" required data-testid="inquiry-resident-age" {...errAttrs("resident_age")} value={form.resident_age} onChange={(e) => set("resident_age")(e.target.value)} />
                <ErrText k="resident_age" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inquiry-care-type">Type of care needed</Label>
              <Select value={form.care_type} onValueChange={set("care_type")}>
                <SelectTrigger id="inquiry-care-type" data-testid="inquiry-care-type" {...errAttrs("care_type")}><SelectValue placeholder="Select care type" /></SelectTrigger>
                <SelectContent>{CARE_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <ErrText k="care_type" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="inquiry-timeline">Timeline</Label>
                <Select value={form.timeline} onValueChange={set("timeline")}>
                  <SelectTrigger id="inquiry-timeline" data-testid="inquiry-timeline" {...errAttrs("timeline")}><SelectValue placeholder="When?" /></SelectTrigger>
                  <SelectContent>{TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                <ErrText k="timeline" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inquiry-payment">Payment source</Label>
                <Select value={form.payment} onValueChange={set("payment")}>
                  <SelectTrigger id="inquiry-payment" data-testid="inquiry-payment" {...errAttrs("payment")}><SelectValue placeholder="How paid?" /></SelectTrigger>
                  <SelectContent>{PAYMENTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                <ErrText k="payment" />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inquiry-message">Additional details</Label>
            <Textarea id="inquiry-message" required rows={3} data-testid="inquiry-message" {...errAttrs("message")} placeholder="Tell them about the situation, mobility, medical needs, etc..." value={form.message} onChange={(e) => set("message")(e.target.value)} />
            <ErrText k="message" />
          </div>
          <Button type="submit" disabled={loading} data-testid="inquiry-submit" className="w-full bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send inquiry</>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
